import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr, TypeUtils } from '@utils';
import { ResetPasswordRequest } from '@services';
import { Grid, Stack } from '@mui/material';
import { VoltaageModalDialog, IVoltaageModalDialogProps, FormikTextfield, FormikSubmitButton } from '@components/common';

type EntityType = ResetPasswordRequest;
type FormFields = Extendable<ResetPasswordRequest>;

export interface IAdminResetPasswordDialogProps extends IVoltaageModalDialogProps {
	onPrimaryButtonClick?: (request: EntityType) => void;
}

export const AdminResetPasswordDialog: FC<IAdminResetPasswordDialogProps> = ({ onPrimaryButtonClick, ...props }) => {
	const { t } = useTranslation('portal');
	const { t: tCommon } = useTranslation('common');
	const { open } = props;
	const formik = useFormik<FormFields>({
		initialValues: { email: '', password: '' },
		validateOnChange: true,
		validationSchema: yup.object({
			email: yup.string().email().required(t('field-required')),
			password: yup.string().required(t('field-required'))
		}),
		onSubmit: ({ email, password }, api) => {
			api.setSubmitting(false);
			onPrimaryButtonClick?.(TypeUtils.convert(ResetPasswordRequest, { email, password }));
		}
	});

	useEffect(() => {
		if (open) {
			formik.resetForm({ values: { email: '', password: '' } });
			formik.validateForm();
		}
	}, [open]);

	return (
		<VoltaageModalDialog title={<Tr.Portal path="organizations.reset-password-modal-title" />} dividers {...props}>
			<form onSubmit={formik.handleSubmit}>
				<Stack direction="column" sx={{ paddingX: 8, paddingY: 4, width: 1.0 }} spacing={3}>
					<Grid container rowSpacing={2} columnSpacing={3}>
						<Grid item xs={6}>
							<FormikTextfield
								formikApi={formik}
								name="email"
								title={tCommon('email')}
								placeholder={tCommon('email')}
								fullWidth
								required
							/>
						</Grid>
						<Grid item xs={6}>
							<FormikTextfield
								formikApi={formik}
								name="password"
								title={tCommon('password')}
								placeholder={tCommon('password')}
								fullWidth
								required
							/>
						</Grid>
					</Grid>

					<FormikSubmitButton formik={formik} data-testid="admin-reset-password-form-submit-button">
						<Tr.Portal path="reset" />
					</FormikSubmitButton>
				</Stack>
			</form>
		</VoltaageModalDialog>
	);
};
